import { ReactNode, createContext, useContext, useMemo } from "react";
import { useSyncWizardController } from "./useSyncWizardController";

type SyncWizardContextValue = ReturnType<typeof useSyncWizardController>;

const SyncWizardContext = createContext<SyncWizardContextValue | null>(null);

export const SyncWizardProvider = ({
  value,
  children,
}: {
  value: SyncWizardContextValue;
  children: ReactNode;
}) => <SyncWizardContext.Provider value={value}>{children}</SyncWizardContext.Provider>;

const useSyncWizardContext = () => {
  const context = useContext(SyncWizardContext);
  if (!context) {
    throw new Error("SyncWizardContext is not available");
  }
  return context;
};

export const useSyncWizardState = () => useSyncWizardContext().state;
export const useSyncWizardData = () => useSyncWizardContext().data;
export const useSyncWizardActions = () => useSyncWizardContext().actions;
export const useSyncWizardMeta = () => {
  const { canNext, stepTitles } = useSyncWizardContext();
  return { canNext, stepTitles };
};

export const useSyncWizardStep1ViewModel = () => {
  const state = useSyncWizardState();
  const data = useSyncWizardData();
  const actions = useSyncWizardActions();

  return useMemo(
    () => ({
      currentBoardName: data.boardName,
      items: data.visibleItems,
      selected: state.selectedItems,
      setSelected: actions.setSelectedItems,
      searchTerm: state.searchTerm,
      onSearchTermChange: actions.setSearchTerm,
      isLoading: data.isPageLoading,
      currentPage: state.currentPage,
      pageCount: data.pageCount,
      hasMore: data.hasMore,
      onPrev: actions.goToPrevPage,
      onNext: actions.goToNextPage,
      hasError: data.hasItemsError,
      onRetry: actions.handleRetryItems,
    }),
    [state, data, actions],
  );
};

export const useSyncWizardStep2ViewModel = () => {
  const state = useSyncWizardState();
  const data = useSyncWizardData();
  const actions = useSyncWizardActions();

  return useMemo(
    () => ({
      currentBoard: data.currentBoard,
      workspaceListdata: data.workspaceListdata,
      isWorkspacesLoading: data.isWorkspacesLoading,
      selectedWorkspace: state.selectedWorkspace,
      setSelectedWorkspace: actions.setSelectedWorkspace,
      targetBoard: state.targetBoard,
      setTargetBoard: actions.setTargetBoard,
      targetGroup: state.targetGroup,
      setTargetGroup: actions.setTargetGroup,
      processSubitems: state.processSubitems,
      processUpdates: state.processUpdates,
      setProcessSubitems: actions.setProcessSubitems,
      setProcessUpdates: actions.setProcessUpdates,
      hasWorkspaceError: data.hasWorkspaceError,
      onRetryWorkspaces: actions.handleRetryWorkspaces,
    }),
    [state, data, actions],
  );
};

export const useSyncWizardStep3ViewModel = () => {
  const data = useSyncWizardData();
  const actions = useSyncWizardActions();

  return useMemo(
    () => ({
      sharedColumns: data.sharedColumns,
      unmatchedColumns: data.unmatchedColumns,
      isLoading: data.isSharedColumnsTourLoading,
      hasError: data.hasSharedColumnsError,
      onRetry: actions.handleRetrySharedColumns,
    }),
    [data, actions],
  );
};

export const useSyncWizardStep4ViewModel = () => {
  const state = useSyncWizardState();
  const data = useSyncWizardData();

  return useMemo(
    () => ({
      currentBoardName: data.boardName,
      selectedItems: state.selectedItems,
      targetBoard: state.targetBoard,
      targetGroup: state.targetGroup,
      confirmed: state.confirmed,
      submitErrorMessage: state.submitErrorMessage,
    }),
    [state, data],
  );
};

